// frontend-dash/src/App.js
// Dashboard App - Standalone deployment at dash.pfadfinder-wutoeschingen.org

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import getMd3Theme from './theme/md3Theme';

import Dashboard from './pages/Dashboard';
import DashboardLayout from './pages/DashboardLayout';
import PrivateRoute from './components/PrivateRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import { AuthProvider } from './context/AuthContext';
import { ThemeContext, ThemeProvider as CustomThemeProvider } from './context/ThemeContext';
import { SettingsProvider } from './context/SettingsContext';

import AdminUsers from './pages/AdminUsers';
import AdminEvents from './pages/AdminEvents';
import AdminBlog from './pages/AdminBlog';
import Notifications from './pages/Notifications';
import Mailbox from './pages/Mailbox';
import ContactRequests from './pages/ContactRequests';
import AdminTools from './pages/AdminTools';
import MyRegistrations from './pages/MyRegistrations';
import MyTeamPage from './pages/MyTeamPage';
import AdminGroups from './pages/AdminGroups';
import Attendance from './pages/Attendance';
import MyChildren from './pages/MyChildren';
import PendingRegistrations from './pages/PendingRegistrations';
import Tasks from './pages/Tasks';
import Resources from './pages/Resources';
import EventTemplates from './pages/EventTemplates';
import AuditLog from './pages/AuditLog';
import Documents from './pages/Documents';
import EventRegistrationsPage from './pages/EventRegistrationsPage';
import EditBlogPost from './pages/EditBlogPost';
import CreateBlogPage from './pages/CreateBlogPage';
import UserDetailPage from './pages/UserDetailPage';
import EditAvatar from './pages/EditAvatar';
import EditableEventPage from './pages/EditableEventPage';
import EditableBlogPage from './pages/EditableBlogPage';
import EditableTeamPage from './pages/EditableTeamPage';
import HomepageSettings from './pages/HomepageSettings';
import AdminIntegrations from './pages/AdminIntegrations';
import InstagramGenerator from './pages/InstagramGenerator';
import AiAgent from './pages/AiAgent';
import FinanceManagement from './pages/FinanceManagement';
import MaterialManagement from './pages/MaterialManagement';
// import Messages from './pages/Messages';

import PageTracker from './components/PageTracker';
import ScrollToTop from './components/ScrollToTop';
import ConsentBanner from './components/ConsentBanner';
import FeedbackBanner from './components/FeedbackBanner';
import { initGA } from './utils/analytics';
import './index.css';
import axios from 'axios';
import { schemeToCssVars, applyCssVars, applyTheme } from './utils/colorGenerator';
import { applyBranding } from './utils/branding';

function AppContent() {
  const { mode } = React.useContext(ThemeContext);
  const theme = React.useMemo(() => getMd3Theme(mode), [mode]);

  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    const applyGlobalSettings = async () => {
      try {
        const rawApiUrl = process.env.REACT_APP_API_URL || '/api';
        const cleanApiUrl = rawApiUrl.endsWith('/api') ? rawApiUrl : `${rawApiUrl}/api`;
        const { data } = await axios.get(`${cleanApiUrl}/settings/homepage`);
        if (data?.colorTheme) {
          applyTheme(data.colorTheme);
        }
        if (data?.organization) {
          applyBranding({
            title: data.organization.dashboardTabTitle || (data.organization.stammName ? `Dashboard | ${data.organization.stammName}` : undefined),
            faviconUrl: data.organization.faviconUrl
          });
        }
      } catch (err) {
        console.error('Failed to load global settings', err);
      }
    };
    applyGlobalSettings();
  }, []);

  const basename = typeof window !== 'undefined' && window.location.pathname.startsWith('/dashboard') ? '/dashboard' : '';

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router basename={basename}>
          <ScrollToTop />
          <PageTracker />
          <div className="flex flex-col min-h-screen">
            <main className="flex-grow">
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />

                {/* Dashboard routes - all protected */}
                <Route path="/" element={<PrivateRoute><DashboardLayout /></PrivateRoute>}>
                  <Route index element={<Dashboard />} />
                  {/* Users / Profiles */}
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="users/:id" element={<UserDetailPage />} />
                  <Route path="user/:id" element={<UserDetailPage />} />
                  <Route path="admin-users" element={<AdminUsers />} />
                  <Route path="members" element={<Navigate to="/users" replace />} />
                  <Route path="profile" element={<UserDetailPage />} />
                  <Route path="account" element={<Navigate to="/profile" replace />} />
                  <Route path="settings" element={<Navigate to="/profile" replace />} />
                  <Route path="my-children" element={<MyChildren />} />
                  <Route path="children" element={<Navigate to="/my-children" replace />} />
                  <Route path="my-team-page" element={<MyTeamPage />} />
                  <Route path="my-registrations" element={<MyRegistrations />} />
                  <Route path="pending-registrations" element={<PendingRegistrations />} />
                  <Route path="registrations" element={<Navigate to="/pending-registrations" replace />} />

                  {/* Events & Calendar */}
                  <Route path="events" element={<AdminEvents />} />
                  <Route path="admin-events" element={<AdminEvents />} />
                  <Route path="events/new" element={<Navigate to="/events/edit/new" replace />} />
                  <Route path="events/create" element={<Navigate to="/events/edit/new" replace />} />
                  <Route path="create-event" element={<Navigate to="/events/edit/new" replace />} />
                  <Route path="calendar" element={<Navigate to="/events" replace />} />
                  <Route path="kalender" element={<Navigate to="/events" replace />} />
                  <Route path="termine" element={<Navigate to="/events" replace />} />
                  <Route path="events/:id/registrations" element={<EventRegistrationsPage />} />
                  <Route path="event-templates" element={<EventTemplates />} />
                  <Route path="templates" element={<Navigate to="/event-templates" replace />} />

                  {/* Blog & Social Media */}
                  <Route path="blog" element={<AdminBlog />} />
                  <Route path="admin-blog" element={<AdminBlog />} />
                  <Route path="blog/new" element={<CreateBlogPage />} />
                  <Route path="blog/create" element={<CreateBlogPage />} />
                  <Route path="create-blog" element={<CreateBlogPage />} />
                  <Route path="blog/edit/:id" element={<EditBlogPost />} />
                  <Route path="blog/:id" element={<EditBlogPost />} />
                  <Route path="instagram-generator" element={<InstagramGenerator />} />
                  <Route path="tools/instagram-generator" element={<InstagramGenerator />} />
                  <Route path="social-generator" element={<InstagramGenerator />} />
                  <Route path="instagram" element={<Navigate to="/instagram-generator" replace />} />
                  <Route path="social" element={<Navigate to="/instagram-generator" replace />} />

                  {/* Groups & Attendance */}
                  <Route path="groups" element={<AdminGroups />} />
                  <Route path="admin-groups" element={<AdminGroups />} />
                  <Route path="stufen" element={<Navigate to="/groups" replace />} />
                  <Route path="sippen" element={<Navigate to="/groups" replace />} />
                  <Route path="attendance" element={<Attendance />} />
                  <Route path="anwesenheit" element={<Navigate to="/attendance" replace />} />

                  {/* Tasks */}
                  <Route path="tasks" element={<Tasks />} />
                  <Route path="todos" element={<Navigate to="/tasks" replace />} />

                  {/* Material & Inventory */}
                  <Route path="inventory" element={<MaterialManagement />} />
                  <Route path="material" element={<MaterialManagement />} />
                  <Route path="inventar" element={<Navigate to="/inventory" replace />} />
                  <Route path="resources" element={<Resources />} />
                  <Route path="verleih" element={<Navigate to="/resources" replace />} />

                  {/* Finances */}
                  <Route path="finances" element={<FinanceManagement />} />
                  <Route path="kasse" element={<Navigate to="/finances" replace />} />
                  <Route path="kassenbuch" element={<Navigate to="/finances" replace />} />

                  {/* Communication & Notifications */}
                  <Route path="notifications" element={<Notifications />} />
                  <Route path="mitteilungen" element={<Navigate to="/notifications" replace />} />
                  <Route path="mailbox" element={<Mailbox />} />
                  <Route path="inbox" element={<Mailbox />} />
                  <Route path="postfach" element={<Navigate to="/mailbox" replace />} />
                  <Route path="messages" element={<Navigate to="/mailbox" replace />} />
                  <Route path="contact-requests" element={<ContactRequests />} />
                  <Route path="contact" element={<Navigate to="/contact-requests" replace />} />
                  <Route path="contacts" element={<Navigate to="/contact-requests" replace />} />

                  {/* AI Agent */}
                  <Route path="ai-agent" element={<AiAgent />} />
                  <Route path="bob" element={<Navigate to="/ai-agent" replace />} />
                  <Route path="ki" element={<Navigate to="/ai-agent" replace />} />

                  {/* Documents & Audit */}
                  <Route path="documents" element={<Documents />} />
                  <Route path="docs" element={<Navigate to="/documents" replace />} />
                  <Route path="downloads" element={<Navigate to="/documents" replace />} />
                  <Route path="audit-log" element={<AuditLog />} />
                  <Route path="audit" element={<Navigate to="/audit-log" replace />} />
                  <Route path="logs" element={<Navigate to="/audit-log" replace />} />

                  {/* Admin Tools & Settings */}
                  <Route path="admin-tools" element={<AdminTools />} />
                  <Route path="admin-tools/domain" element={<AdminTools />} />
                  <Route path="admin-tools/domains" element={<Navigate to="/admin-tools/domain" replace />} />
                  <Route path="domain" element={<Navigate to="/admin-tools/domain" replace />} />
                  <Route path="domains" element={<Navigate to="/admin-tools/domain" replace />} />
                  <Route path="admin-tools/integrations" element={<AdminTools />} />
                  <Route path="admin-tools/secrets" element={<Navigate to="/admin-tools/integrations" replace />} />
                  <Route path="integrations" element={<Navigate to="/admin-tools/integrations" replace />} />
                  <Route path="secrets" element={<Navigate to="/admin-tools/integrations" replace />} />
                  <Route path="admin-tools/codes" element={<AdminTools />} />
                  <Route path="codes" element={<Navigate to="/admin-tools/codes" replace />} />
                  <Route path="admin-tools/emails" element={<AdminTools />} />
                  <Route path="emails" element={<Navigate to="/admin-tools/emails" replace />} />
                  <Route path="admin-tools/llms" element={<AdminTools />} />
                  <Route path="admin-tools/robots" element={<Navigate to="/admin-tools/llms" replace />} />
                  <Route path="admin-tools/bob" element={<AdminTools />} />
                  <Route path="admin-tools/analytics" element={<AdminTools />} />
                  <Route path="analytics" element={<Navigate to="/admin-tools/analytics" replace />} />
                  <Route path="admin-tools/homepage" element={<HomepageSettings />} />
                  <Route path="homepage" element={<Navigate to="/admin-tools/homepage" replace />} />
                  <Route path="baukasten" element={<Navigate to="/admin-tools/homepage" replace />} />
                </Route>

                <Route path="edit-avatar" element={<PrivateRoute><EditAvatar /></PrivateRoute>} />

                {/* Full-screen event editor — no DashboardLayout */}
                <Route path="events/edit/:id" element={<PrivateRoute><EditableEventPage /></PrivateRoute>} />
                <Route path="events/new" element={<PrivateRoute><EditableEventPage /></PrivateRoute>} />
                <Route path="events/:id" element={<PrivateRoute><EditableEventPage /></PrivateRoute>} />

                {/* Inline Edit Routes — full-screen, no DashboardLayout */}
                <Route path="edit/events/:slug" element={<PrivateRoute><EditableEventPage /></PrivateRoute>} />
                <Route path="edit/blog/:id" element={<PrivateRoute><EditableBlogPage /></PrivateRoute>} />
                <Route path="edit/t/:id" element={<PrivateRoute><EditableTeamPage /></PrivateRoute>} />
                <Route path="t/:id" element={<PrivateRoute><EditableTeamPage /></PrivateRoute>} />

                {/* Catch-all: redirect to dashboard home */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </main>
          </div>
          <ConsentBanner />
          <FeedbackBanner />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <SettingsProvider>
      <CustomThemeProvider>
        <AppContent />
      </CustomThemeProvider>
    </SettingsProvider>
  );
}

export default App;
